/**
 * Thin wrappers over the deck CRUD HTTP surface in ``thread-api``.
 *
 * Each function returns the parsed response body (or throws on non-2xx).
 * Auth bearer-token injection lives in ``../../api/client`` — we don't
 * re-implement it here.
 */

import { apiClient, getAuthToken } from "../../api/client";
import type {
  Deck,
  DeckCreatePayload,
  DeckMetaUpdatePayload,
  DeckSummary,
  SlideInsertPayload,
  SlideInsertResult,
  SlideReorderPayload,
  SlideUpdatePayload,
} from "./types";

export async function listDecksForProject(
  projectId: string,
): Promise<DeckSummary[]> {
  return apiClient<DeckSummary[]>(`/projects/${projectId}/decks`);
}

export async function fetchDeck(deckId: string): Promise<Deck> {
  return apiClient<Deck>(`/decks/${deckId}`);
}

export async function createDeck(
  projectId: string,
  payload: DeckCreatePayload,
): Promise<Deck> {
  return apiClient<Deck>(`/projects/${projectId}/decks`, {
    method: "POST",
    body: payload,
  });
}

export async function updateDeckMeta(
  deckId: string,
  payload: DeckMetaUpdatePayload,
  ifMatchVersion?: number,
): Promise<Deck> {
  const headers: Record<string, string> = {};
  if (typeof ifMatchVersion === "number") {
    headers["If-Match"] = String(ifMatchVersion);
  }
  return apiClient<Deck>(`/decks/${deckId}`, {
    method: "PATCH",
    body: payload,
    headers,
  });
}

export async function updateSlide(
  deckId: string,
  slideIdx: number,
  payload: SlideUpdatePayload,
  ifMatchVersion?: number,
): Promise<Deck> {
  const headers: Record<string, string> = {};
  if (typeof ifMatchVersion === "number") {
    headers["If-Match"] = String(ifMatchVersion);
  }
  return apiClient<Deck>(`/decks/${deckId}/slides/${slideIdx}`, {
    method: "PUT",
    body: payload,
    headers,
  });
}

/**
 * Insert a slide. The server returns the rehydrated deck plus the final
 * idx the slide landed at in the ``X-Slide-Idx`` response header — we
 * read it directly so the caller doesn't have to reconcile clamping
 * rules on its own. Falls back to the predicted idx (deck.slides.length
 * before insert) if the header is missing for any reason.
 */
export async function insertSlide(
  deckId: string,
  payload: SlideInsertPayload,
  ifMatchVersion?: number,
): Promise<SlideInsertResult> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (typeof ifMatchVersion === "number") {
    headers["If-Match"] = String(ifMatchVersion);
  }

  const base = import.meta.env.VITE_API_URL ?? "/api";
  const res = await fetch(`${base}/decks/${deckId}/slides`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = "Unknown error";
    try {
      const body = await res.clone().json();
      const raw = body?.detail;
      if (typeof raw === "string") detail = raw;
      else if (raw) detail = JSON.stringify(raw);
    } catch {
      detail = await res.text().catch(() => detail);
    }
    throw new Error(`API ${res.status}: ${detail}`);
  }
  const deck = (await res.json()) as Deck;
  const headerIdx = res.headers.get("X-Slide-Idx");
  const newIdx = headerIdx !== null
    ? Number(headerIdx)
    : Math.max(0, deck.deck_json.slides.length - 1);
  return { deck, newIdx };
}

export async function deleteSlide(
  deckId: string,
  slideIdx: number,
  ifMatchVersion?: number,
): Promise<Deck> {
  const headers: Record<string, string> = {};
  if (typeof ifMatchVersion === "number") {
    headers["If-Match"] = String(ifMatchVersion);
  }
  return apiClient<Deck>(`/decks/${deckId}/slides/${slideIdx}`, {
    method: "DELETE",
    headers,
  });
}

export async function reorderSlides(
  deckId: string,
  payload: SlideReorderPayload,
  ifMatchVersion?: number,
): Promise<Deck> {
  const headers: Record<string, string> = {};
  if (typeof ifMatchVersion === "number") {
    headers["If-Match"] = String(ifMatchVersion);
  }
  return apiClient<Deck>(`/decks/${deckId}/slides/reorder`, {
    method: "POST",
    body: payload,
    headers,
  });
}

export async function deleteDeck(deckId: string): Promise<void> {
  await apiClient<void>(`/decks/${deckId}`, { method: "DELETE" });
}

/**
 * Resolved URL for the editor iframe — the renderer endpoint that pulls
 * the deck through the IP-safety gate and emits the slides HTML. Cached
 * busted via ``cacheTag`` so the iframe reloads on save.
 */
export function deckRenderUrl(
  deckId: string,
  cacheTag?: string,
  layout: "desktop" | "mobile" | "edit" = "desktop",
): string {
  const base = `/api/decks/${deckId}/render`;
  // ``desktop`` = the 3D word-substrate runtime (the presentation view; the
  // only one that exposes ``window.__threadPresentation`` for slide nav).
  // ``mobile`` = the theme's linear card renderer (no Three.js, but its own
  // word-positioning + reveal hides body text). ``edit`` = the dedicated
  // server-rendered plain layout (FXC-8818): every slide's annotated bodyHtml
  // as plain, full-width, scrollable HTML in its own
  // ``<section data-slide-idx>`` — no runtime at all, so a real click lands on
  // real, sized, editable text. The editor passes ``edit`` when edit mode is
  // on (the 3D runtime collapses the editable source to 0×0 behind its
  // canvas; see ADR-002). EDIT in plain, PRESENT in 3D.
  const params = new URLSearchParams({ layout });
  if (cacheTag) params.set("v", cacheTag);
  return `${base}?${params.toString()}`;
}
